import { useRef, useState } from 'react'

import { EMPTY_LIST } from '../../constants/common'
import { getItemsFromApiResponse } from '../../utils/api'
import { useIsMountedRef } from '../useIsMountedRef'
import { useNotification } from '../useNotification'

import {
  appendItems,
  checkInfiniteItemsFetchDataIsLastPage,
  getPermissions,
  getPreparedItems,
  getTotalItemsCountData,
  isInfiniteItemsLoadCurrent,
} from './useInfiniteItemsFetchData.utils'

export function useInfiniteItemsFetchData({
  apiParams,
  checkIsLastPage = checkInfiniteItemsFetchDataIsLastPage,
  getData,
  getDataItems = getItemsFromApiResponse,
  getMergedItems = appendItems,
  getPermissionsData = getPermissions,
  getTotalCountData = getTotalItemsCountData,
  initialItems = EMPTY_LIST,
  setAdditionalData,
  toPreparedItems = getPreparedItems,
  withPagination,
  onChangeInitialized,
  onChangePermissions,
  onChangeTotalCount,
}) {
  const { handleAddErrorNotification } = useNotification()

  const isMountedRef = useIsMountedRef()

  const [loadingMore, setLoadingMore] = useState(false)
  const [loadingInitial, setLoadingInitial] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [items, setItems] = useState(null)

  const hasMoreRef = useRef(true)
  const pageNumRef = useRef(1)
  const loadGenerationRef = useRef(0)

  function fetchData(params) {
    return getData({
      ...apiParams,
      ...(withPagination ? { page: pageNumRef.current } : null),
      ...params,
    })
  }

  function applyMoreItems(res) {
    const nextItems = toPreparedItems(getDataItems(res))

    setItems(prevItems => getMergedItems(prevItems, nextItems))

    setAdditionalData?.(res)

    onChangePermissions?.(getPermissionsData(res))

    hasMoreRef.current = !checkIsLastPage(res, getDataItems)

    if (withPagination) {
      pageNumRef.current = pageNumRef.current + 1
    }

    return nextItems
  }

  function loadMore(params) {
    if (!loadingMore) {
      const generation = loadGenerationRef.current

      setLoadingMore(true)

      return fetchData(params)
        .then(res =>
          isInfiniteItemsLoadCurrent({
            generation,
            isMountedRef,
            loadGenerationRef,
          })
            ? applyMoreItems(res)
            : null
        )
        .catch(() => {
          if (
            isInfiniteItemsLoadCurrent({
              generation,
              isMountedRef,
              loadGenerationRef,
            })
          ) {
            handleAddErrorNotification('Error.CommonApiRequestFailKey')
          }
        })
        .finally(() => {
          if (
            isInfiniteItemsLoadCurrent({
              generation,
              isMountedRef,
              loadGenerationRef,
            })
          ) {
            setLoadingMore(false)
          }
        })
    }
  }

  function loadInitial(params) {
    loadGenerationRef.current += 1

    const generation = loadGenerationRef.current

    pageNumRef.current = 1

    setLoadingInitial(true)
    setLoadingMore(false)
    setHasError(false)

    return fetchData(params)
      .then(res => {
        const nextItems = [
          ...initialItems,
          ...toPreparedItems(getDataItems(res)),
        ]

        if (
          isInfiniteItemsLoadCurrent({
            generation,
            isMountedRef,
            loadGenerationRef,
          })
        ) {
          onChangeInitialized?.(true)

          setItems(nextItems)

          setAdditionalData?.(res)

          const nextTotalCount = getTotalCountData(res)

          setTotalCount(nextTotalCount)

          onChangePermissions?.(getPermissionsData(res))

          onChangeTotalCount?.(nextTotalCount)

          hasMoreRef.current = !checkIsLastPage(res, getDataItems)

          if (withPagination) {
            pageNumRef.current = pageNumRef.current + 1
          }
        }

        return nextItems
      })
      .catch(() => {
        if (
          isInfiniteItemsLoadCurrent({
            generation,
            isMountedRef,
            loadGenerationRef,
          })
        ) {
          setHasError(true)

          handleAddErrorNotification('Error.CommonApiRequestFailKey')
        }
      })
      .finally(() => {
        if (
          isInfiniteItemsLoadCurrent({
            generation,
            isMountedRef,
            loadGenerationRef,
          })
        ) {
          setLoadingInitial(false)
        }
      })
  }

  return {
    hasError,
    hasMoreRef,
    items,
    loadingInitial,
    loadingMore,
    loadInitial,
    loadMore,
    pageNumRef,
    setItems,
    totalCount,
  }
}
